<template>
  <div>
    <v-server-table
      id="iframeBiosignalsScanPacksTable"
      ref="iframeBiosignalsScanPacksTable"
      name="iframeBiosignalsScanPacksTable"
      :columns="columns"
      :options="tableOptions"
      @filter="onFilterUpdate"
    >
      <span slot="entity" class="no-break" slot-scope="props">{{ handleEntityName(props.row) }}</span>
      <span slot="start" class="no-break" slot-scope="props">{{ props.row.start | localTime('YYYY-MM-DD HH:mm')
        }}</span>
      <span slot="validUntil" class="no-break"
            slot-scope="props">{{ props.row.validUntil | localTime('YYYY-MM-DD HH:mm') }}</span>
      <span slot="expired" :style="{ color: props.row.expired ? '#d33b3b' : '#60a841' }" class="no-break"
            slot-scope="props">{{ handleCodeStatus(props.row) }}</span>
      <span slot="testFlow" class="no-break"
            slot-scope="props">{{ props.row.testFlow ? `${props.row.testFlow.name} (${props.row.testFlow.hash})` : 'N/A'
        }}</span>
      <span slot="type" class="no-break" slot-scope="props">{{ $t(`biosigns.packs.type.${props.row.type}`) }}</span>

      <span slot="actions" class="no-break" slot-scope="props">
        <va-popover
          placement="left"
          :title="$t('buttons.refresh')"
        >
          <i
            class="fa fa-pen clickable"
            @click.prevent="handleRefresh(props.row)"
          />
        </va-popover>

        <va-popover
          v-if="!props.row.expired"
          class="pl-2"
          placement="left"
          :title="$t('biosigns.packs.inactivate')"
        >
          <i
            class="fa fa-ban clickable"
            @click.prevent="handleExpire(props.row)"
          />
        </va-popover>
      </span>

    </v-server-table>

    <ConfirmationModal
      v-if="showConfirmationModal && hasWritePermissions"
      :title="$t('biosigns.packs.inactivate')"
      :body="$t('biosigns.packs.inactivateBody', {name: selectedPack.name})"
      okClass="btn-danger"
      @modal-confirmed="onConfirmInactivate"
      @modal-canceled="onCancelInactivate"
    />
  </div>
</template>

<script>
import TableQueryMixin from '@/mixins/TableQueryMixin.vue';
import MultiselectLabelFormatter from '@/mixins/MultiselectLabelFormatter.vue';
import ClustersAndEntitiesMixin from '@/mixins/ClustersAndEntitiesMixin.vue';
import RangeDatePicker from '@/components/common/RangeDatePicker.vue';
import Multiselect from 'vue-multiselect';
import LocalStorageHelper from '@/local-storage/LocalStorageHelper';
import { TABLE_IFRAME_BIOSIGNALS_ACCESS_CODES } from '@/local-storage/LocalStorageKeys';
import { get as _get } from 'lodash';
import { IframeService } from '@/api/IframeService';
import ConfirmationModal from '@/components/common/ConfirmationModal.vue';

export default {
  name: 'BiosignsScanPacksTable',
  components: { ConfirmationModal, Multiselect, RangeDatePicker },
  mixins: [
    TableQueryMixin,
    MultiselectLabelFormatter,
    ClustersAndEntitiesMixin,
  ],
  props: {
    filters: {
      type: Object,
      required: true,
    },
    clientOptions: {
      type: Array,
      required: true,
    },
    component: {
      type: Object,
      required: true
    },
    serviceSlug: {
      type: String,
      required: true
    },
  },
  data: function() {
    return {
      entitiesOptions: [],
      queryUrl: `${this.$store.state.iframe_server}v1/biosignals-scan-packs`,
      columns: ['name', 'entity', 'testFlow', 'type', 'start', 'validUntil', 'expired', 'totalNrScans', 'usedScans'],
      tableOptions: {
        orderBy: {
          column: 'start',
          ascending: false,
        },
        saveState: false,
        storage: 'local',
        filterable: true,
        sortable: ['start', 'validUntil'],
        customFilters: ['entities', 'statuses'],
        headings: {
          name: this.$t('accounts.name'),
          entity: this.$t('accounts.entity'),
          start: this.$t('activities_agenda.table.start'),
          validUntil: this.$t('biosigns.accessCodes.columns.validUntil'),
          expired: this.$t('biosigns.accessCodes.columns.status'),
          testFlow: this.$t('biosigns.accessCodes.columns.testFlow'),
          totalNrScans: this.$t('clinical.waiting_room_statistics.total'),
          usedScans: this.$t('biosigns.accessCodes.packs.used'),
          type: this.$t('biosigns.packs.type.title'),
          actions: this.$t('clinical.labels.actions'),
        },
        columnsClasses: {
          name: 'alignCenter',
          validUntil: 'alignCenter',
          expired: 'alignCenter',
          testFlow: 'alignCenter',
          start: 'alignCenter',
          totalNrScans: 'alignCenter',
          usedScans: 'alignCenter',
          entity: 'alignCenter',
          type: 'alignCenter',
          actions: 'alignCenter',
        },
      },
      showConfirmationModal: false,
      selectedPack: null,

    };
  },
  mounted() {
    if(this.hasWritePermissions) {
      this.columns.push('actions')
    }
  },
  methods: {
    onConfirmInactivate: async function() {
      if (this.selectedPack) {
        this.showConfirmationModal = true;
        try {
          await IframeService.deactivatePack(this.selectedPack.id);
          this.$emit('refreshTable');
          this.showConfirmationModal = false;
          this.selectedPack = null;
        } catch (err) {
          console.log(err);
          this.showToast(this.$t('entities.messages.error'), this.$store.state.errorToastOptions);
        }
      }
    },
    onCancelInactivate: function() {
      this.showConfirmationModal = false;
      this.selectedPack = null;
    },
    handleCodeStatus: function(pack) {
      if (pack.expired) {
        return this.$t('biosigns.accessCodes.statuses.expired');
      }
      return this.$t('biosigns.accessCodes.statuses.active');
    },
    handleEntityName: function(pack) {
      const client = this.clientOptions.find(client => client.id === pack.testFlow.client_id);
      return client ? client.name : 'N/A';
    },
    handleRefresh: function(pack) {
      this.$emit('handleUpdatePack', pack);
    },
    handleExpire: function(pack) {
      this.selectedPack = pack;
      this.showConfirmationModal = true;
    },
  },
  computed: {
    hasWritePermissions: function() {
      console.log(this.component)
      if(this.component && this.component.service && this.component.service.user_permissions.length > 0) {
        let perms = this.component.service.user_permissions.filter(perm => perm.info_type === 130)

        if(perms && perms.length > 0) {
          return perms.some(perm => perm.access_type >= 2)
        }
      }
      return false
    }
  },
  watch: {
    'filters.statuses': function(value) {
      if (value) {
        this.emitTableFilter(`vue-tables.iframeBiosignalsScanPacksTable.filter::statuses`, value.map(elem => elem.slug).join(',') || '');
      } else {
        this.emitTableFilter(`vue-tables.iframeBiosignalsScanPacksTable.filter::statuses`, '');
      }
    },
    'filters.entities': function(value) {
      if (value) {
        //const parsedIds = value.map(elem => parseInt(elem.id)).join(',')
        //this.emitTableFilter(`vue-tables.iframeBiosignalsAccessCodes.filter::entities`, parsedIds || '')
        this.emitTableFilter(`vue-tables.iframeBiosignalsScanPacksTable.filter::entities`, value.id || '');
      } else {
        this.emitTableFilter(`vue-tables.iframeBiosignalsScanPacksTable.filter::entities`, '');
      }
    },
  },
};
</script>

<style lang="scss">
.alignCenter {
  text-align: center;
}
</style>
